/**
 * Analytics fixtures. Same env switch as the queue/history mocks
 * (`VITE_ALERTS_USE_MOCKS`, scenario via `VITE_ALERTS_MOCK_SCENARIO`).
 *
 * Scenarios map to the cases the ticket calls out:
 *   default      day with shifts, one in progress, reviewed > received
 *   empty        a day with no period summary at all
 *   unavailable  current workload whose carryover could not be computed
 *   failure      `location_timezone_not_configured`
 *   truncated    out-of-range period rejection
 */
import { AlertApiError } from "@/alerts/api/envelope.ts";
import type {
  AlertAnalyticsFilterState,
  AlertAnalyticsOutcomeCounts,
  AlertAnalyticsPeriodReport,
  AlertAnalyticsResponse,
  AlertAnalyticsShift,
} from "@/alerts/types/alerts-analytics.ts";

const env = import.meta.env as Record<string, string | undefined>;
const scenario = (): string => env.VITE_ALERTS_MOCK_SCENARIO || "default";

const T0 = Date.parse("2026-07-17T10:42:18Z");
const iso = (offsetMinutes: number): string => new Date(T0 + offsetMinutes * 60_000).toISOString();

const outcomes = (reviewed: number): AlertAnalyticsOutcomeCounts => ({
  confirmed_signal: Math.round(reviewed * 0.34),
  false_alert: Math.round(reviewed * 0.4),
  duplicate: Math.round(reviewed * 0.06),
  insufficient_evidence: Math.round(reviewed * 0.12),
  other: Math.round(reviewed * 0.05),
  unclassified_legacy: Math.max(
    0,
    reviewed -
      (Math.round(reviewed * 0.34) +
        Math.round(reviewed * 0.4) +
        Math.round(reviewed * 0.06) +
        Math.round(reviewed * 0.12) +
        Math.round(reviewed * 0.05)),
  ),
});

const trend = (buckets: number, received: number, reviewed: number) =>
  Array.from({ length: buckets }, (_, i) => ({
    start_time: iso(-(buckets - i) * 60),
    end_time: iso(-(buckets - i - 1) * 60),
    // Deterministic wobble so the two lines cross — reviewed legitimately
    // exceeds received when operators clear carryover.
    alerts_received: Math.max(0, Math.round(received / buckets + ((i * 7) % 5) - 2)),
    alerts_reviewed: Math.max(0, Math.round(reviewed / buckets + ((i * 5) % 6) - 2)),
  }));

const report = (received: number, reviewed: number): AlertAnalyticsPeriodReport => {
  const mix = outcomes(reviewed);
  return {
    queue_flow: {
      pending_at_start: 18,
      alerts_received: received,
      alerts_reviewed: reviewed,
      net_queue_change: received - reviewed,
      pending_at_effective_end: 18 + received - reviewed,
    },
    reviewer_breakdown: {
      alerts_reviewed: reviewed,
      reviewers: [
        {
          principal_type: "user",
          principal_id: "u-101",
          principal_label: "Anna Dvořáková",
          role_code: "operator",
          reviews_completed: Math.round(reviewed * 0.5),
          initial_outcome_counts: outcomes(Math.round(reviewed * 0.5)),
        },
        {
          principal_type: "user",
          principal_id: "u-207",
          // Deliberately unnamed — the UI must fall back, never invent a name.
          principal_label: null,
          role_code: "supervisor",
          reviews_completed: Math.round(reviewed * 0.3),
          initial_outcome_counts: outcomes(Math.round(reviewed * 0.3)),
        },
      ],
      other_reviewers: null,
      unattributed: {
        reviews_completed: Math.max(
          0,
          reviewed - Math.round(reviewed * 0.5) - Math.round(reviewed * 0.3),
        ),
        initial_outcome_counts: outcomes(
          Math.max(0, reviewed - Math.round(reviewed * 0.5) - Math.round(reviewed * 0.3)),
        ),
      },
      truncated: false,
    },
    outcomes: {
      alerts_reviewed: reviewed,
      items: (Object.keys(mix) as Array<keyof AlertAnalyticsOutcomeCounts>).map((code) => ({
        code,
        count: mix[code],
        share: reviewed === 0 ? null : mix[code] / reviewed,
      })),
      legacy_defaulted_disposition_count: 3,
      unclassified_legacy_count: mix.unclassified_legacy,
    },
    trend: trend(12, received, reviewed),
    coverage: { status: "complete", excluded_legacy_reviews: 0, effective_start_time: iso(-720) },
  };
};

const SHIFTS: AlertAnalyticsShift[] = [
  {
    shift_key: "00_06",
    label: "00:00–06:00",
    status: "complete",
    start_time: iso(-640),
    end_time: iso(-280),
    effective_end_time: iso(-280),
    period_summary: report(24, 31),
  },
  {
    shift_key: "06_12",
    label: "06:00–12:00",
    status: "in_progress",
    start_time: iso(-280),
    end_time: iso(80),
    effective_end_time: iso(0),
    period_summary: report(17, 10),
  },
  {
    shift_key: "12_18",
    label: "12:00–18:00",
    status: "not_started",
    start_time: iso(80),
    end_time: iso(440),
    effective_end_time: null,
    period_summary: null,
  },
];

const latency = () => new Promise((resolve) => setTimeout(resolve, 200));

export const mockAlertAnalytics = async (
  state: AlertAnalyticsFilterState,
): Promise<AlertAnalyticsResponse> => {
  await latency();
  const s = scenario();

  if (s === "failure") {
    throw new AlertApiError("Location timezone is not configured.", 400, null, {
      code: "location_timezone_not_configured",
    });
  }
  if (s === "truncated") {
    throw new AlertApiError("Requested period is outside the supported window.", 400, null, {
      code: "time_range_out_of_bounds",
      constraint: "max_lookback",
      max_days: 40,
      earliest_start_time: iso(-40 * 24 * 60),
    });
  }

  const period = state.period;
  const isDay = period.mode === "day";
  return {
    as_of: iso(0),
    scope: {
      location_id: state.scope.location_id,
      machine_ids: state.scope.machine_ids ?? [],
      device_mxids: [],
      type_in: state.scope.type_in ?? [],
    },
    period: {
      mode: isDay ? "local_day" : "absolute_range",
      local_date: period.mode === "day" ? period.local_date : null,
      start_time: iso(-640),
      end_time: iso(800),
      effective_end_time: iso(0),
      provisional: isDay,
    },
    current_workload: {
      pending_count: 12,
      oldest_pending: {
        alert_id: 48291,
        waiting_since: iso(-108),
        waiting_since_basis: "pending_since",
        estimated: false,
      },
      carryover:
        s === "unavailable"
          ? { count: null, estimated_count: null, unavailable_reason: "Location timezone missing." }
          : { count: 4, estimated_count: 1, block_started_at: iso(-640) },
    },
    // Reviewed deliberately exceeds received: carryover from an earlier period
    // is legitimately cleared here, and the UI must not treat it as an error.
    period_summary: s === "empty" ? null : report(41, 48),
    // Only a local day maps onto a shift schedule.
    shift_breakdown: isDay && s !== "empty" ? SHIFTS : null,
  };
};
